/**
 * The shapes the permission catalog is written in, and the one helper that
 * writes the common view / create / edit / delete quartet.
 *
 * Split from `permissions.ts` only so the catalog itself fits in two readable
 * files; see that file for what a permission is and how one is added.
 */

export interface PermissionDef {
  /** Short label for the matrix column — "View", "Approve". */
  readonly action: string;
  readonly description: string;
  /** Granted automatically alongside this one: editing a thing implies seeing it. */
  readonly requires?: readonly string[];
  /** Shown with a warning badge: money, every patient's data, other people's figures. */
  readonly sensitive?: true;
}

interface PermissionItem {
  readonly label: string;
  readonly permissions: Readonly<Record<string, PermissionDef>>;
}

export interface PermissionModule {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  readonly items: readonly PermissionItem[];
}

type CrudKeys<P extends string> = `${P}.${'view' | 'create' | 'edit' | 'delete'}`;

/** view / create / edit / delete for one catalogue-style resource. */
export function crud<P extends string>(
  prefix: P,
  noun: string,
): Readonly<Record<CrudKeys<P>, PermissionDef>> {
  const view = `${prefix}.view`;
  // Computed keys widen the literal to a string index, which TypeScript will not
  // narrow back to the four keys it plainly has — hence the double assertion.
  return {
    [view]: { action: 'View', description: `See the ${noun} list and open a record.` },
    [`${prefix}.create`]: { action: 'Create', description: `Add a new ${noun}.`, requires: [view] },
    [`${prefix}.edit`]: {
      action: 'Edit',
      description: `Change an existing ${noun}.`,
      requires: [view],
    },
    [`${prefix}.delete`]: { action: 'Delete', description: `Remove a ${noun}.`, requires: [view] },
  } as unknown as Record<CrudKeys<P>, PermissionDef>;
}
